import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  type PlatformTemplate,
  whatsappService,
} from "@/services/whatsapp.service/whatsapp.api";
import { zodResolver } from "@hookform/resolvers/zod";
import { Hash, Loader2, Pencil, PlusCircle, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

const PLACEHOLDER_TOKENS = [
  { token: "{{nome_cliente}}", label: "Nome do cliente" },
  { token: "{{link_chamado}}", label: "Link do chamado" },
  { token: "{{link_portal}}", label: "Link do portal" },
  { token: "{{horario_funcionamento}}", label: "Horário de funcionamento" },
  { token: "{{proximo_atendimento}}", label: "Próximo atendimento" },
];

const KIND_LABEL: Record<string, string> = {
  onboarding: "Como acompanhar pelo portal",
  ticket_created: "Aviso de ticket criado",
  out_of_hours: "Mensagem fora do horário de atendimento",
  custom: "Personalizado",
};

/** Campo de corpo com botões pra inserir os placeholders nomeados no cursor. */
function PlatformTemplateBodyField({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  function insertToken(token: string) {
    const el = textareaRef.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const next = value.slice(0, start) + token + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      el?.focus();
      const pos = start + token.length;
      el?.setSelectionRange(pos, pos);
    });
  }

  return (
    <div className="space-y-2">
      <div className="rounded-md border border-dashed border-border bg-muted/40 p-3">
        <p className="mb-2 text-xs text-muted-foreground">
          Clique para inserir uma variável no texto — ela é substituída pelo dado real na hora do
          envio.
        </p>
        <div className="flex flex-wrap gap-1.5">
          {PLACEHOLDER_TOKENS.map((v) => (
            <Button
              key={v.token}
              type="button"
              size="sm"
              variant="secondary"
              className="h-7 gap-1 px-2 text-xs"
              onClick={() => insertToken(v.token)}
            >
              <Hash className="h-3 w-3" />
              {v.label}
            </Button>
          ))}
        </div>
      </div>
      <Textarea
        ref={textareaRef}
        placeholder="Olá {{nome_cliente}}! Acompanhe por aqui: {{link_chamado}}"
        className="min-h-[100px]"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

const formSchema = z.object({
  label: z.string().min(1, "Nome obrigatório"),
  body: z.string().min(1, "Corpo da mensagem obrigatório"),
});

type FormValues = z.infer<typeof formSchema>;

function PlatformTemplateForm({
  title,
  initial,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  title: string;
  initial?: { label: string; body: string };
  submitLabel: string;
  onSubmit: (values: FormValues) => Promise<void>;
  onCancel: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { label: initial?.label || "", body: initial?.body || "" },
  });

  async function handleSubmit(values: FormValues) {
    setSubmitting(true);
    await onSubmit(values);
    setSubmitting(false);
  }

  return (
    <div className="rounded-lg border border-border p-4">
      <h4 className="mb-4 font-medium">{title}</h4>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="label"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nome</FormLabel>
                <FormControl>
                  <Input placeholder="Ex: Aviso de manutenção" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="body"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Corpo da mensagem</FormLabel>
                <FormControl>
                  <PlatformTemplateBodyField value={field.value} onChange={field.onChange} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex gap-2">
            <Button type="submit" disabled={submitting} className="gap-2">
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {submitLabel}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancelar
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

export default function PlatformTemplates() {
  const [templates, setTemplates] = useState<PlatformTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<PlatformTemplate | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    const [data, err] = await whatsappService.listPlatformTemplates();
    setLoading(false);
    if (err) {
      toast.error("Erro ao buscar templates de plataforma");
      return;
    }
    setTemplates(Array.isArray(data) ? data : []);
  }, []);

  useEffect(() => {
    void fetchTemplates();
  }, [fetchTemplates]);

  async function handleCreate(values: FormValues) {
    const [res, err] = await whatsappService.createPlatformTemplate(values);
    if (err || !res) {
      toast.error(err?.msg || "Erro ao criar template");
      return;
    }
    toast.success("Template criado!");
    setShowForm(false);
    void fetchTemplates();
  }

  async function handleEdit(values: FormValues) {
    if (!editing) return;
    const [res, err] = await whatsappService.updatePlatformTemplate(editing._id, values);
    if (err || !res) {
      toast.error(err?.msg || "Erro ao salvar template");
      return;
    }
    toast.success("Template salvo!");
    setEditing(null);
    void fetchTemplates();
  }

  async function handleToggleActive(t: PlatformTemplate) {
    setTogglingId(t._id);
    const [res, err] = await whatsappService.updatePlatformTemplate(t._id, { active: !t.active });
    setTogglingId(null);
    if (err || !res) {
      toast.error(err?.msg || "Erro ao atualizar template");
      return;
    }
    void fetchTemplates();
  }

  async function handleDelete(t: PlatformTemplate) {
    setDeletingId(t._id);
    const [res, err] = await whatsappService.deletePlatformTemplate(t._id);
    setDeletingId(null);
    if (err || !res?.success) {
      toast.error(err?.msg || "Erro ao excluir template");
      return;
    }
    toast.success(`"${t.label}" removido`);
    void fetchTemplates();
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">Templates de plataforma</h3>
          <p className="text-xs text-muted-foreground">
            Não precisam de aprovação da Meta — só podem ser usados com a conversa já aberta
            (janela de 24h).
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setEditing(null);
            setShowForm((v) => !v);
          }}
          className="gap-2"
        >
          <PlusCircle className="h-4 w-4" />
          Novo template
        </Button>
      </div>

      {editing && (
        <PlatformTemplateForm
          title={`Editar "${editing.label}"`}
          initial={{ label: editing.label, body: editing.body }}
          submitLabel="Salvar"
          onSubmit={handleEdit}
          onCancel={() => setEditing(null)}
        />
      )}

      {!editing && showForm && (
        <PlatformTemplateForm
          title="Criar template"
          submitLabel="Criar"
          onSubmit={handleCreate}
          onCancel={() => setShowForm(false)}
        />
      )}

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </div>
      ) : templates.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Nenhum template de plataforma encontrado.
        </div>
      ) : (
        <div className="space-y-2">
          {templates.map((t) => {
            const isSystem = t.kind !== "custom";
            const isTogglingThis = togglingId === t._id;
            const isDeletingThis = deletingId === t._id;
            return (
              <div
                key={t._id}
                className="flex items-center justify-between gap-3 rounded-lg border border-border p-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-medium text-sm">{t.label}</p>
                    {isSystem && (
                      <Badge variant="secondary" className="shrink-0 text-[11px]">
                        Padrão do sistema
                      </Badge>
                    )}
                    {!t.active && (
                      <Badge variant="outline" className="shrink-0 text-[11px] text-muted-foreground">
                        Desativado
                      </Badge>
                    )}
                  </div>
                  <p className="mt-1 truncate text-xs text-muted-foreground">{t.body}</p>
                  {isSystem && (
                    <p className="mt-0.5 text-[11px] text-muted-foreground">{KIND_LABEL[t.kind]}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {isTogglingThis ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : (
                    <Switch checked={t.active} onCheckedChange={() => void handleToggleActive(t)} />
                  )}
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    aria-label="Editar"
                    onClick={() => {
                      setShowForm(false);
                      setEditing(t);
                    }}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  {!isSystem && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          aria-label="Excluir"
                          disabled={isDeletingThis}
                        >
                          {isDeletingThis ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Excluir "{t.label}"?</AlertDialogTitle>
                          <AlertDialogDescription>Essa ação não pode ser desfeita.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => void handleDelete(t)}>
                            Excluir
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
